import { useEffect, useState } from "react";
import Swal from "sweetalert2";

import { functionsHttpsCallable } from "../FirebaseStorage/firebase";

/**
 * Si la creación de APEs en el legacy está habilitada o no, como DATO y no como código.
 *
 * Antes esto era una cirugía de 4 archivos que había que revertir y volver a desplegar cada vez
 * que se cambiaba de opinión: se hizo cinco veces entre el 2026-08-16 y el 2026-08-24. Ahora es el
 * campo booleano `crearAPE` del documento `empresas/{codigoEmpresa}` y cambiarlo no toca código.
 *
 * Copia el patrón que ya existía en el sistema: `cargueContratoHabilitado`, en ese mismo documento,
 * gatea la pantalla de cargue de contratos igual que esto gatea la creación.
 *
 * No hace falta desplegar NADA de Firebase: `ape_getEstadoEmpresa` ya está desplegada y hace
 * `return estado.data()` —el documento completo, sin proyección—, así que un campo nuevo en
 * Firestore llega al frontend solo. Y por eso mismo no se lee Firestore desde el cliente: las
 * reglas del proyecto niegan todo y no hay razón para tocarlas.
 */

const getEstadoEmpresa = functionsHttpsCallable("ape_getEstadoEmpresa");

/** Destino del aviso mientras John no corte el DNS de sysconfi.confipetrol.com. */
const URL_SYSCONFI = "https://sysconfi.cloud";

/**
 * @param {number|string} codigoEmpresa normalmente `userData.codigoEmpresa`
 * @returns {{habilitado: boolean, cargando: boolean}}
 *
 * Arranca y falla en `false` (cerrado) a propósito: si no se puede confirmar que la creación está
 * permitida, no se permite. Es el mismo criterio que ya usa el cargue de contratos, que deja el
 * botón deshabilitado mientras no haya llegado el dato.
 */
export function useCrearApeHabilitado(codigoEmpresa) {
	const [estado, setEstado] = useState({ habilitado: false, cargando: true });

	useEffect(() => {
		// Evita escribir el estado si la pantalla se desmontó antes de que respondiera.
		let vivo = true;

		(async () => {
			try {
				const respuesta = await getEstadoEmpresa({ cod_empr: parseInt(codigoEmpresa) });
				const empresa = (respuesta && respuesta.data && respuesta.data.data) || {};
				if (vivo) {
					// Estricto: cualquier cosa que no sea el booleano true —ausente, null, la
					// cadena "true"— deja la creación cerrada.
					setEstado({ habilitado: empresa.crearAPE === true, cargando: false });
				}
			} catch (error) {
				console.warn("[flag] no se pudo leer crearAPE:", (error && error.code) || error);
				if (vivo) {
					setEstado({ habilitado: false, cargando: false });
				}
			}
		})();

		return () => {
			vivo = false;
		};
	}, [codigoEmpresa]);

	return estado;
}

/**
 * Aviso que sale al pulsar el botón de crear cuando la creación está cerrada. El botón se deja en
 * su sitio y con los mismos roles a propósito: si desapareciera, quien lo usaba a diario lo leería
 * como que el sistema se rompió.
 */
export function avisoCreacionEnSysConfi() {
	Swal.fire({
		title: "Las APEs nuevas se crean en SysConfi",
		html: 'Aquí puedes seguir gestionando las APEs que ya están en proceso.<br/><br/><b>sysconfi.cloud</b>',
		icon: "info",
		showCancelButton: true,
		confirmButtonText: "Ir a SysConfi",
		cancelButtonText: "Cerrar",
	}).then((result) => {
		if (result.isConfirmed) {
			window.open(URL_SYSCONFI, "_blank", "noopener");
		}
	});
}
